import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { designStorageService } from '../utils/designStorageService';
import { designsAPI } from '../utils/apiService';
import { useAuth } from './AuthContext';

const WizardContext = createContext();

const MAX_HISTORY_SIZE = 20;

const getInitialState = () => ({
  currentWizard: 'A',
  currentStep: 0,
  wizardHighestSteps: { A: 0, B: 0, C: 0, E: 0 },
  wizardA: {
    intake: {
      aim: '',
      dataType: '',
      questionTypes: [],
      setting: '',
      timeConstraints: '',
      generalisability: ''
    },
    philosophicalDimensions: {
      ontology: '',
      epistemology: '',
      axiology: ''
    },
    recommendedParadigm: '',
    philosophy: '',
    theoryApproach: '',
    methodologicalChoice: '',
    mixedMethodsPriority: '',
    mixedMethodsTiming: '',
    mixedMethodsIntegration: '',
    strategy: '',
    strategyDetails: {},
    timeHorizon: '',
    timeHorizonDetails: {},
    techniques: {
      dataCollection: [],
      sampling: '',
      samplingDetails: {},
      sampleSizesPerMethod: {},
      analysis: [],
      qualityCriteria: []
    }
  },
  wizardB: {
    analysisApproach: '',
    qualitativeMethods: {
      thematicAnalysis: false,
      contentAnalysis: false,
      sentimentAnalysis: false,
      narrativeAnalysis: false,
      discourseAnalysis: false,
      groundedTheory: false,
      phenomenologicalAnalysis: false
    },
    quantitativeMethods: {
      descriptiveStats: {
        enabled: false,
        categories: []
      },
      inferentialTests: {
        enabled: false,
        tests: []
      }
    },
    dataRequirements: {
      qualitative: [],
      quantitative: []
    },
    questionFamily: '',
    questionFamilies: [],
    variables: {
      dvType: '',
      dvCount: 1,
      ivType: '',
      ivTypes: [],
      ivCount: 1,
      groupsCount: 2,
      independence: '',
      sampleSize: 0
    },
    assumptions: {
      normality: null,
      equalVariances: null,
      independence: null,
      linearity: null,
      adequateCounts: null
    },
    recommendations: {
      primaryTest: '',
      selectedTests: [],
      secondaryTests: [],
      alternatives: [],
      effectSize: '',
      confidenceInterval: 95
    },
    analysisPlan: ''
  },
  wizardC: {
    researchAim: '',
    objectives: ['', '', '', '', '', '', ''],
    constructs: [],
    levels: [],
    theoreticalFoundations: [],
    theoryLinks: {},
    diagramNodes: [],
    diagramConnections: [],
    previewNodePositions: {},
    assumptions: {
      ontological: [],
      epistemological: [],
      axiological: [],
      methodological: []
    },
    propositions: [],
    validationResults: [],
    frameworkText: ''
  },
  dataCollection: {
    viewedSteps: {},
    lastVisitedPathway: null
  },
  wizardD: {
    overview: {
      involvesHumanParticipants: null,
      involvesHumanTissue: null,
      involvesPersonalData: null,
      requiresEthicsApproval: null
    },
    researchTeam: {
      principalInvestigator: {
        firstName: '',
        lastName: '',
        email: '',
        institution: '',
        faculty: '',
        department: '',
        role: 'staff'
      },
      supervisors: [],
      coResearchers: []
    },
    projectSynopsis: {
      title: '',
      startDate: '',
      endDate: '',
      ethicsStartDate: '',
      ethicsEndDate: '',
      fundingSource: '',
      fundingDetails: '',
      summary: '',
      researchQuestions: '',
      methodology: ''
    },
    participants: {
      description: '',
      selectionMethod: '',
      sampleSizeJustification: '',
      vulnerableGroups: {
        includes: false,
        types: [],
        justification: ''
      },
      inclusionCriteria: '',
      exclusionCriteria: ''
    },
    recruitment: {
      methods: [],
      otherMethod: '',
      initialInfo: '',
      consentProcess: '',
      withdrawalProcedure: '',
      debriefProcedure: ''
    },
    dataCollection: {
      methods: [],
      otherMethod: '',
      instruments: '',
      pilotTesting: '',
      interventionDetails: ''
    },
    dataManagement: {
      dataTypes: [],
      recordingMethod: '',
      anonymizationMethod: '',
      storageLocations: [],
      otherStorage: '',
      accessControl: '',
      securityMeasures: '',
      retentionPeriod: '',
      disposalMethod: '',
      gdprCompliant: null,
      privacyNoticeIncluded: null
    },
    riskAssessment: {
      participantRisks: [],
      participantMitigations: '',
      researcherRisks: '',
      researcherMitigations: '',
      incidentResponse: ''
    },
    specialConsiderations: {
      involvesMinors: false,
      minorsDetails: '',
      involvesHumanTissue: false,
      tissueDetails: '',
      involvesClinical: false,
      clinicalDetails: '',
      involvesIncentives: false,
      incentivesDetails: '',
      involvesInternationalData: false,
      internationalDetails: '',
      securitySensitive: false,
      securityDetails: ''
    },
    dissemination: {
      methods: [],
      otherMethod: '',
      dataSharing: '',
      embargoPlans: '',
      participantFeedback: ''
    },
    declarations: {
      confirmedAccuracy: false,
      confirmedGDPR: false,
      confirmedTraining: false,
      additionalComments: ''
    }
  },
  decisions: [],
  wizardAComplete: false,
  wizardBComplete: false,
  wizardCComplete: false,
  dataCollectionComplete: false,
  wizardDComplete: false,
  wizardEComplete: false,
  frameworkComplete: false
});

export const WizardProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [currentDesignId, setCurrentDesignId] = useState(null);
  const [currentDesignName, setCurrentDesignName] = useState('Untitled Design');
  const [savedDesigns, setSavedDesigns] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [state, setState] = useState(getInitialState());
  const [isSyncingToDb, setIsSyncingToDb] = useState(false);
  const dbSaveTimerRef = useRef(null);
  const currentDesignIdRef = useRef(null);
  const currentDesignNameRef = useRef('Untitled Design');

  // Undo/Redo history management
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedoAction = useRef(false);

  // Track if this is the first render to skip initial history save
  const isInitialRender = useRef(true);
  const lastStateJson = useRef('');

  // Helper to add current state to history - called explicitly after data changes
  const addToHistory = useCallback((newState) => {
    if (isUndoRedoAction.current) {
      isUndoRedoAction.current = false;
      return;
    }

    // Check if state actually changed (avoid duplicate entries)
    const stateToCompare = {
      wizardA: newState.wizardA,
      wizardB: newState.wizardB,
      wizardC: newState.wizardC,
      wizardD: newState.wizardD
    };
    const currentStateJson = JSON.stringify(stateToCompare);
    if (currentStateJson === lastStateJson.current) {
      return;
    }
    lastStateJson.current = currentStateJson;

    // Save current state to history
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(JSON.parse(JSON.stringify(newState)));
      if (newHistory.length > MAX_HISTORY_SIZE) {
        newHistory.shift();
        return newHistory;
      }
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, MAX_HISTORY_SIZE - 1));
  }, [historyIndex]);

  // Track wizardA, wizardB, wizardC, and wizardD data changes for history
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      lastStateJson.current = JSON.stringify({ wizardA: state.wizardA, wizardB: state.wizardB, wizardC: state.wizardC, wizardD: state.wizardD });
      return;
    }
    
    addToHistory(state);
  }, [state.wizardA, state.wizardB, state.wizardC, state.wizardD, addToHistory]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const undo = useCallback(() => {
    if (!canUndo) return false;
    
    isUndoRedoAction.current = true;
    const previousState = history[historyIndex - 1];
    setState(previousState);
    setHistoryIndex(prev => prev - 1);
    setHasUnsavedChanges(true);
    return true;
  }, [canUndo, history, historyIndex]);

  const redo = useCallback(() => {
    if (!canRedo) return false;
    
    isUndoRedoAction.current = true;
    const nextState = history[historyIndex + 1];
    setState(nextState);
    setHistoryIndex(prev => prev + 1);
    setHasUnsavedChanges(true);
    return true;
  }, [canRedo, history, historyIndex]);

  const clearHistory = useCallback(() => {
    setHistory([]);
    setHistoryIndex(-1);
  }, []);

  useEffect(() => {
    const designs = designStorageService.getAllDesigns();
    setSavedDesigns(designs);
    
    const savedCurrentId = designStorageService.getCurrentDesignId();
    if (savedCurrentId) {
      const design = designStorageService.getDesignById(savedCurrentId);
      if (design) {
        loadDesignIntoState(design);
        return;
      }
    }
    
    try {
      const sessionState = localStorage.getItem('researchWizardSession');
      if (sessionState) {
        const parsed = JSON.parse(sessionState);
        if (parsed) {
          setState(prev => ({
            ...prev,
            ...parsed
          }));
          return;
        }
      }
      
      const legacyState = localStorage.getItem('researchWizardState');
      if (legacyState) {
        const parsed = JSON.parse(legacyState);
        if (parsed && parsed.wizardA && parsed.wizardB) {
          setState(prev => ({
            ...prev,
            ...parsed
          }));
        }
      }
    } catch (error) {
      console.error('Error loading session state:', error);
    }
  }, []);

  // Keep refs in sync so debounced DB save can read latest values
  useEffect(() => {
    currentDesignIdRef.current = currentDesignId;
  }, [currentDesignId]);

  useEffect(() => {
    currentDesignNameRef.current = currentDesignName;
  }, [currentDesignName]);

  // Persist to localStorage (guest + logged-in backup)
  useEffect(() => {
    if (isInitialRender.current) return;
    
    try {
      const sessionData = {
        currentWizard: state.currentWizard,
        currentStep: state.currentStep,
        wizardHighestSteps: state.wizardHighestSteps,
        wizardA: state.wizardA,
        wizardB: state.wizardB,
        wizardC: state.wizardC,
        dataCollection: state.dataCollection,
        wizardD: state.wizardD,
        wizardAComplete: state.wizardAComplete,
        wizardBComplete: state.wizardBComplete,
        wizardCComplete: state.wizardCComplete,
        dataCollectionComplete: state.dataCollectionComplete,
        wizardDComplete: state.wizardDComplete,
        wizardEComplete: state.wizardEComplete,
        frameworkComplete: state.frameworkComplete,
        decisions: state.decisions
      };
      localStorage.setItem('researchWizardSession', JSON.stringify(sessionData));
    } catch (error) {
      console.error('Error saving session:', error);
    }
  }, [state]);

  // Autosave to DB (logged-in users only) — debounced 3s after last change
  useEffect(() => {
    if (!currentUser || isInitialRender.current) return;

    if (dbSaveTimerRef.current) clearTimeout(dbSaveTimerRef.current);

    dbSaveTimerRef.current = setTimeout(async () => {
      const designId = currentDesignIdRef.current;
      const designName = currentDesignNameRef.current || 'Untitled Design';
      const payload = buildDesignPayload(designId, designName, state);
      try {
        setIsSyncingToDb(true);
        const saved = await designsAPI.save(payload);
        if (!currentDesignIdRef.current) {
          setCurrentDesignId(saved.id);
          setCurrentDesignName(saved.name);
          designStorageService.setCurrentDesignId(saved.id);
        }
      } catch (err) {
        console.error('DB autosave failed:', err);
      } finally {
        setIsSyncingToDb(false);
      }
    }, 3000);

    return () => {
      if (dbSaveTimerRef.current) clearTimeout(dbSaveTimerRef.current);
    };
  }, [state, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load designs from DB when user logs in
  useEffect(() => {
    if (!currentUser) return;
    designsAPI.list()
      .then((designs) => setSavedDesigns(designs))
      .catch((err) => console.error('Failed to load designs from DB:', err));
  }, [currentUser]);

  const buildDesignPayload = (id, name, s, gapFinderData) => ({
    id: id || undefined,
    name: name || 'Untitled Design',
    currentWizard: s.currentWizard,
    currentStep: s.currentStep,
    wizardAComplete: s.wizardAComplete,
    wizardBComplete: s.wizardBComplete,
    wizardCComplete: s.wizardCComplete,
    wizardDComplete: s.wizardDComplete,
    wizardEComplete: s.wizardEComplete,
    frameworkComplete: s.frameworkComplete,
    dataCollectionComplete: s.dataCollectionComplete,
    wizardAData: s.wizardA,
    wizardBData: s.wizardB,
    wizardCData: s.wizardC,
    wizardDData: s.wizardD,
    dataCollectionData: s.dataCollection,
    gapFinderData: gapFinderData || (() => {
      try { return JSON.parse(localStorage.getItem('gapFinder_state_v1') || '{}'); } catch { return {}; }
    })(),
    decisions: s.decisions,
  });

  const loadDesignIntoState = (design) => {
    setCurrentDesignId(design.id);
    setCurrentDesignName(design.name);
    designStorageService.setCurrentDesignId(design.id);
    
    let loadedWizard = design.currentWizard || 'A';
    let loadedStep = design.currentStep || 0;
    
    if (!design.currentWizard && loadedStep > 7 && design.wizardAComplete) {
      loadedWizard = 'B';
      loadedStep = 0;
    }
    
    const defaultWizardC = getInitialState().wizardC;
    const loadedWizardC = design.wizardCData 
      ? { ...defaultWizardC, ...design.wizardCData }
      : defaultWizardC;
    
    const defaultWizardD = getInitialState().wizardD;
    const loadedWizardD = design.wizardDData 
      ? { ...defaultWizardD, ...design.wizardDData }
      : defaultWizardD;
    
    const defaultDataCollection = getInitialState().dataCollection;
    const loadedDataCollection = design.dataCollectionData 
      ? { ...defaultDataCollection, ...design.dataCollectionData }
      : defaultDataCollection;
    
    setState({
      currentWizard: loadedWizard,
      currentStep: loadedStep,
      wizardHighestSteps: { A: 0, B: 0, C: 0, E: 0, [loadedWizard]: loadedStep },
      wizardA: design.wizardAData || getInitialState().wizardA,
      wizardB: design.wizardBData || getInitialState().wizardB,
      wizardC: loadedWizardC,
      dataCollection: loadedDataCollection,
      wizardD: loadedWizardD,
      decisions: design.decisions || [],
      wizardAComplete: design.wizardAComplete || false,
      wizardBComplete: design.wizardBComplete || false,
      dataCollectionComplete: design.dataCollectionComplete || false,
      wizardCComplete: design.wizardCComplete || false,
      wizardDComplete: design.wizardDComplete || false,
      wizardEComplete: design.wizardEComplete || false,
      frameworkComplete: design.frameworkComplete || false
    });
    setHasUnsavedChanges(false);
  };

  const refreshDesignsList = useCallback(async () => {
    if (currentUser) {
      try {
        const designs = await designsAPI.list();
        setSavedDesigns(designs);
        return;
      } catch (err) {
        console.error('Failed to refresh designs from DB:', err);
      }
    }
    const designs = designStorageService.getAllDesigns();
    setSavedDesigns(designs);
  }, [currentUser]);

  const saveCurrentDesign = useCallback(async (name) => {
    const designName = name || currentDesignName || 'Untitled Design';
    const payload = buildDesignPayload(currentDesignId, designName, state);

    if (currentUser) {
      try {
        const saved = await designsAPI.save(payload);
        setCurrentDesignId(saved.id);
        setCurrentDesignName(saved.name);
        designStorageService.setCurrentDesignId(saved.id);
        setHasUnsavedChanges(false);
        refreshDesignsList();
        return saved;
      } catch (err) {
        console.error('DB save failed, falling back to localStorage:', err);
      }
    }

    // localStorage fallback
    let savedDesign;
    if (currentDesignId) {
      const existing = designStorageService.getDesignById(currentDesignId);
      savedDesign = designStorageService.saveDesign({ ...existing, ...payload, name: designName });
    } else {
      savedDesign = designStorageService.createNewDesign(designName);
      savedDesign = designStorageService.saveDesign({ ...savedDesign, ...payload, name: designName });
    }
    setCurrentDesignId(savedDesign.id);
    setCurrentDesignName(savedDesign.name);
    designStorageService.setCurrentDesignId(savedDesign.id);
    setHasUnsavedChanges(false);
    refreshDesignsList();
    return savedDesign;
  }, [currentDesignId, currentDesignName, state, currentUser, refreshDesignsList]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveAsNewDesign = useCallback(async (name) => {
    const designName = name || 'Untitled Design';
    const payload = buildDesignPayload(null, designName, state);

    if (currentUser) {
      try {
        const saved = await designsAPI.save(payload);
        setCurrentDesignId(saved.id);
        setCurrentDesignName(saved.name);
        designStorageService.setCurrentDesignId(saved.id);
        setHasUnsavedChanges(false);
        refreshDesignsList();
        return saved;
      } catch (err) {
        console.error('DB save-as-new failed, falling back to localStorage:', err);
      }
    }

    const newDesign = designStorageService.createNewDesign(designName);
    const savedDesign = designStorageService.saveDesign({ ...newDesign, ...payload, name: designName });
    setCurrentDesignId(savedDesign.id);
    setCurrentDesignName(savedDesign.name);
    designStorageService.setCurrentDesignId(savedDesign.id);
    setHasUnsavedChanges(false);
    refreshDesignsList();
    return savedDesign;
  }, [state, currentUser, refreshDesignsList]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDesign = useCallback(async (id) => {
    if (currentUser) {
      try {
        const design = await designsAPI.get(id);
        if (design) {
          // Restore Gap Finder data to localStorage so GapFinderWizard picks it up
          if (design.gapFinderData && Object.keys(design.gapFinderData).length > 0) {
            localStorage.setItem('gapFinder_state_v1', JSON.stringify(design.gapFinderData));
          }
          loadDesignIntoState(design);
          refreshDesignsList();
          return true;
        }
      } catch (err) {
        console.error('DB load design failed:', err);
      }
    }
    const design = designStorageService.getDesignById(id);
    if (design) {
      loadDesignIntoState(design);
      refreshDesignsList();
      return true;
    }
    return false;
  }, [currentUser, refreshDesignsList]); // eslint-disable-line react-hooks/exhaustive-deps

  const createNewDesign = useCallback(() => {
    setState(getInitialState());
    setCurrentDesignId(null);
    setCurrentDesignName('Untitled Design');
    designStorageService.setCurrentDesignId(null);
    setHasUnsavedChanges(false);
  }, []);

  const deleteDesign = useCallback(async (id) => {
    if (currentUser) {
      try {
        await designsAPI.delete(id);
      } catch (err) {
        console.error('DB delete failed:', err);
      }
    }
    designStorageService.deleteDesign(id);
    if (id === currentDesignId) {
      createNewDesign();
    }
    refreshDesignsList();
  }, [currentDesignId, currentUser, createNewDesign, refreshDesignsList]);

  const renameDesign = useCallback(async (id, newName) => {
    if (currentUser) {
      try {
        await designsAPI.rename(id, newName);
      } catch (err) {
        console.error('DB rename failed:', err);
      }
    }
    designStorageService.renameDesign(id, newName);
    if (id === currentDesignId) {
      setCurrentDesignName(newName);
    }
    refreshDesignsList();
  }, [currentDesignId, currentUser, refreshDesignsList]);

  const exportDesign = useCallback((id) => {
    designStorageService.exportDesign(id || currentDesignId);
  }, [currentDesignId]);

  const importDesign = useCallback(async (file) => {
    const design = await designStorageService.importDesign(file);
    refreshDesignsList();
    return design;
  }, [refreshDesignsList]);

  const updateData = useCallback((updates) => {
    setHasUnsavedChanges(true);
    setState(prev => {
      const newState = { 
        ...prev,
        wizardA: { ...prev.wizardA },
        wizardB: { ...prev.wizardB },
        wizardC: { ...prev.wizardC },
        wizardD: { ...prev.wizardD }
      };
      
      Object.keys(updates).forEach(key => {
        if (key.startsWith('wizardA.')) {
          const path = key.split('.');
          if (path.length === 2) {
            newState.wizardA = { ...newState.wizardA, [path[1]]: updates[key] };
          } else if (path.length === 3) {
            const nestedKey = path[1];
            newState.wizardA = {
              ...newState.wizardA,
              [nestedKey]: {
                ...(newState.wizardA[nestedKey] || {}),
                [path[2]]: updates[key]
              }
            };
          }
        } else if (key.startsWith('wizardB.')) {
          const path = key.split('.');
          if (path.length === 2) {
            newState.wizardB = { ...newState.wizardB, [path[1]]: updates[key] };
          } else if (path.length === 3) {
            const nestedKey = path[1];
            newState.wizardB = {
              ...newState.wizardB,
              [nestedKey]: {
                ...(newState.wizardB[nestedKey] || {}),
                [path[2]]: updates[key]
              }
            };
          }
        } else if (key.startsWith('wizardC.')) {
          const path = key.split('.');
          if (path.length === 2) {
            newState.wizardC = { ...newState.wizardC, [path[1]]: updates[key] };
          } else if (path.length === 3) {
            const nestedKey = path[1];
            newState.wizardC = {
              ...newState.wizardC,
              [nestedKey]: {
                ...(newState.wizardC[nestedKey] || {}),
                [path[2]]: updates[key]
              }
            };
          }
        } else if (key.startsWith('wizardD.')) {
          const path = key.split('.');
          if (path.length === 2) {
            newState.wizardD = { ...newState.wizardD, [path[1]]: updates[key] };
          } else if (path.length === 3) {
            const nestedKey = path[1];
            newState.wizardD = {
              ...newState.wizardD,
              [nestedKey]: {
                ...(newState.wizardD[nestedKey] || {}),
                [path[2]]: updates[key]
              }
            };
          }
        } else if (key.startsWith('wizardE.')) {
          // wizardE paths map to wizardD storage (Ethics Wizard E data stored in wizardD)
          const path = key.split('.');
          if (path.length === 2) {
            newState.wizardD = { ...newState.wizardD, [path[1]]: updates[key] };
          } else if (path.length === 3) {
            const nestedKey = path[1];
            newState.wizardD = {
              ...newState.wizardD,
              [nestedKey]: {
                ...(newState.wizardD[nestedKey] || {}),
                [path[2]]: updates[key]
              }
            };
          }
        } else {
          newState[key] = updates[key];
        }
      });
      
      return newState;
    });
  }, []);

  const logDecision = useCallback((step, decision, rationale) => {
    setHasUnsavedChanges(true);
    setState(prev => ({
      ...prev,
      decisions: [...prev.decisions, {
        timestamp: new Date().toISOString(),
        wizard: prev.currentWizard,
        step,
        decision,
        rationale
      }]
    }));
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => {
      const newStep = prev.currentStep + 1;
      const wiz = prev.currentWizard;
      const prevHighest = prev.wizardHighestSteps?.[wiz] ?? 0;
      return {
        ...prev,
        currentStep: newStep,
        wizardHighestSteps: {
          ...prev.wizardHighestSteps,
          [wiz]: Math.max(prevHighest, newStep)
        }
      };
    });
    window.scrollTo(0, 0);
  }, []);

  const previousStep = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      currentStep: prev.currentStep > 0 ? prev.currentStep - 1 : 0 
    }));
    window.scrollTo(0, 0);
  }, []);

  const goToStep = useCallback((step) => {
    setState(prev => ({ ...prev, currentStep: step }));
    window.scrollTo(0, 0);
  }, []);

  const switchWizard = useCallback((wizard) => {
    setState(prev => ({ 
      ...prev, 
      currentWizard: wizard,
      currentStep: 0
    }));
    window.scrollTo(0, 0);
  }, []);

  const resetWizard = useCallback(() => {
    setState(getInitialState());
    setCurrentDesignId(null);
    setCurrentDesignName('Untitled Design');
    setHasUnsavedChanges(false);
  }, []);

  const resetCurrentWizard = useCallback(() => {
    setHasUnsavedChanges(true);
    setState(prev => {
      const newState = { ...prev };
      const initial = getInitialState();
      if (prev.currentWizard === 'A') {
        newState.wizardA = initial.wizardA;
        newState.wizardAComplete = false;
      } else {
        newState.wizardB = initial.wizardB;
        newState.wizardBComplete = false;
      }
      newState.currentStep = 0;
      newState.decisions = newState.decisions.filter(d => d.wizard !== prev.currentWizard);
      return newState;
    });
  }, []);

  const completeWizard = useCallback((wizardToComplete) => {
    setHasUnsavedChanges(true);
    setState(prev => ({
      ...prev,
      [`wizard${wizardToComplete}Complete`]: true
    }));
  }, []);

  const setFrameworkComplete = useCallback((complete) => {
    setHasUnsavedChanges(true);
    setState(prev => ({
      ...prev,
      frameworkComplete: complete
    }));
  }, []);

  const currentData = state.currentWizard === 'A' ? state.wizardA : 
                     state.currentWizard === 'B' ? state.wizardB : 
                     state.currentWizard === 'C' ? state.wizardC : 
                     state.currentWizard === 'DC' ? state.dataCollection : state.wizardD;
  
  const updateDataSimple = useCallback((key, value) => {
    const prefix = state.currentWizard === 'A' ? 'wizardA' : 
                   state.currentWizard === 'B' ? 'wizardB' : 
                   state.currentWizard === 'C' ? 'wizardC' : 
                   state.currentWizard === 'DC' ? 'dataCollection' : 'wizardD';
    updateData({ [`${prefix}.${key}`]: value });
  }, [state.currentWizard, updateData]);
  
  const updateDataCollection = useCallback((updates) => {
    setHasUnsavedChanges(true);
    setState(prev => ({
      ...prev,
      dataCollection: {
        ...prev.dataCollection,
        ...updates
      }
    }));
  }, []);

  const setDataCollectionComplete = useCallback((complete) => {
    setHasUnsavedChanges(true);
    setState(prev => ({
      ...prev,
      dataCollectionComplete: complete
    }));
  }, []);
  
  const highestStep = state.wizardHighestSteps?.[state.currentWizard] ?? 0;

  const value = {
    ...state,
    data: currentData,
    dataA: state.wizardA,
    dataB: state.wizardB,
    dataC: state.wizardC,
    dataDC: state.dataCollection,
    dataE: state.wizardD,
    
    currentDesignId,
    currentDesignName,
    savedDesigns,
    hasUnsavedChanges,
    isSyncingToDb,
    
    updateData: updateDataSimple,
    updateDataRaw: updateData,
    logDecision,
    
    nextStep,
    previousStep,
    prevStep: previousStep,
    goToStep,
    switchWizard,
    highestStep,
    
    resetWizard,
    resetCurrentWizard,
    completeWizard,
    
    saveCurrentDesign,
    saveAsNewDesign,
    loadDesign,
    createNewDesign,
    deleteDesign,
    renameDesign,
    exportDesign,
    importDesign,
    refreshDesignsList,
    
    // Undo/Redo
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory,
    
    // Framework
    setFrameworkComplete,
    
    // Data Collection
    updateDataCollection,
    setDataCollectionComplete,
    
    warnings: [],
    explanations: []
  };

  return (
    <WizardContext.Provider value={value}>
      {children}
    </WizardContext.Provider>
  );
};

export const useWizard = () => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
};

export default WizardContext;
